// src/com/trucker_trans_plan/TruckerTransPlan.js
import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/system';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {
    ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord, TopCode } from '../../value/AppConst';
import {
    arriveInfoListToSelectStoreRepo, bulkCompanyProductsListRepo, comInfoListRepo,
    transPlanTsrListParamRepo, transPlanTsrListRepo, userInfoRepo,
    vehInfoListOfDcRepo, transPlanChangedRepo,
    dcInfoListRepo
} from '../../value/AppModel';
import { LIST_TRANS_PLAN_TSR } from '../trans_plan_tsr/TransPlanTsrGql';
import { TransPlanTsrDictionary } from '../trans_plan_tsr/TransPlanTsrDict';
import { useAlertSetter } from '../../api/app-hook';
import { LIST_ARRIVE_INFO_TO_SELECT_STORE } from '../arrive_info/ArriveInfoGql';
import ValueMisc from '../../value/ValueMisc';
import AutoTextField from '../../common/AutoTextField';
import VzmDateField from '../../common/VzmDateField';
import IsValid from '../../value/IsValid';
import StoreHouseSvg from '../../asset/image/store_house.svg';
import {
    LIST_BULK_COMPANY_PRODUCTS
} from '../bulk_company_products/BulkCompanyProductsGql';
import TruckerTransPlanItem from './TruckerTransPlanItem';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import { devideVehiclesByCompany, LIST_VEH_INFO_OF_DC } from '../veh_info/VehInfoGql';
import {
    OFF_TRANS_PLAN_TRUCK, SET_TRANS_PLAN_COMPANY, SET_TRANS_PLAN_TRUCK
} from '../trans_plan/TransPlanGql';
import { LIST_DC_INFO } from '../dc_info/DcInfoGql';

const vehSorter = (a, b) => {
    const aNew = a.vehPlates.indexOf('신규') > -1;
    const bNew = b.vehPlates.indexOf('신규') > -1;
    if(aNew && !bNew) return 1;
    if(!aNew && bNew) return -1;
    return a.vehPlates.localeCompare(b.vehPlates);
};

/**
 * 운송사 홈플러스 배차등록.
 * @returns 
 */
export default function TruckerTransPlan() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const dcCd = sessionInfo.getDcCd();
    const transPlanChanged = useReactiveVar(transPlanChangedRepo);
    const storeList = useReactiveVar(arriveInfoListToSelectStoreRepo);
    const dcInfoListData = useReactiveVar(dcInfoListRepo); // 기본온도검색용.
    const transPlanTsrListParam = useReactiveVar(transPlanTsrListParamRepo);
    const transPlanTsrListData = useReactiveVar(transPlanTsrListRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const bulkCompanyProductsListData = useReactiveVar(bulkCompanyProductsListRepo);
    const vehInfoListOfDcData = useReactiveVar(vehInfoListOfDcRepo);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const transPlanTsrListParamRef = useRef(transPlanTsrListParam);
    const skipInitTransPlanChangedRef = useRef(true); // transPlanChanged 이벤트 초기회피
    useEffect(() => {
        transPlanTsrListParamRef.current = transPlanTsrListParam;
    }, [transPlanTsrListParam]);

    // ##### Call GraphQL to get List #####
    const [getTransPlanTsrList, responseList] = useLazyQuery(LIST_TRANS_PLAN_TSR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: TransPlanTsrDictionary.errorTitle.Get, message: [error.message]})
    });
    // Get store list from arrive_info table.
    const [getArriveInfoList, responseStoreList] = useLazyQuery(LIST_ARRIVE_INFO_TO_SELECT_STORE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreList(data, option)},
        onError: (error) => addAlert({title: '도착지 목록 조회 오류', message: [error.message]})
    });
        // TransPlanTsrEditorGroup 내에서 온도설정 등을 위해 DC_INFO 데이터 필요.
    const [getDcInfoList, responseDcList] = useLazyQuery(LIST_DC_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetDcList(data, option)},
        onError: (error) => addAlert({title: 'DC정보 조회 오류', message: [error.message]})
    });
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });
    const [getVehInfoListOfDc, responseVehInfoList] = useLazyQuery(LIST_VEH_INFO_OF_DC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehInfoList(data, option)},
        onError: (error) => addAlert({title: "차량목록 조회 오류", message: [error.message]})
    });
    // 982 Bulk 상품 목록 조회
    const [getBulkCompanyProductsList, responseBulkCompanyProductsList] = useLazyQuery(LIST_BULK_COMPANY_PRODUCTS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetBulkCompanyProductsList(data, option)},
		onError: (error) => addAlert({title: '벌크상품 목록 조회 오류', message: [error.message]})
    });
    const [setTransPlanTruck, responseSetTruck] = useMutation( SET_TRANS_PLAN_TRUCK, {
        onCompleted: (data, option) => onCompleteSetTruck(data, option),
        onError: (error) => addAlert({title: "차량배정 오류", message: [error.message]})
    });
    const [offTransPlanTruck, responseOffTruck] = useMutation( OFF_TRANS_PLAN_TRUCK, {
        onCompleted: (data, option) => onCompleteOffTruck(data, option),
        onError: (error) => addAlert({title: "차량배정 초기화 오류", message: [error.message]})
    });
    const [setTransPlanCompany, responseSetCompany] = useMutation( SET_TRANS_PLAN_COMPANY, {
        onCompleted: (data, option) => onCompleteSetCompany(data, option),
        onError: (error) => addAlert({title: "운송사 변경 오류", message: [error.message]})
    })

    const getTransPlanTsrListNow = (storeId, planDate) => {
        if(!planDate) {
            planDate = ValueMisc.getYMD();
        }
        const param = {variables:{dcCd:dcCd, storeId:storeId, planDate:planDate}};
        getTransPlanTsrList(param);
    };

    const comInfoList = useMemo(()=>{
        if(comInfoListData.records.length > 0) {
            const list = comInfoListData.records.filter(com=>com.dcCd===dcCd && com.useYn==='Y') //[...comInfoListData.records];
            list.unshift({comId:AppWord.ChooseValue, comName:AppWord.ChooseKorean});
            return list;
        }
        return [{comId:AppWord.ChooseValue, comName:AppWord.ChooseKorean}];
    }, [comInfoListData]);

    // 날짜, 점포 변경 시 TSR 조회 호출
    // 다른 화면에서 transPlanTsrListParam 설정하고 이 화면으로 들어오면 바로 조회 가능함.
    useEffect(() => {
        if(transPlanTsrListParam.storeId) {
            if(transPlanTsrListParam.planDate) {
                getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate);
            }
            else if(!transPlanTsrListParam.planDate) {
                const newParam = {...transPlanTsrListParam, planDate:ValueMisc.getYMD()};
                transPlanTsrListParamRepo(newParam);
            }
        }
    }, [transPlanTsrListParam]);

    // 먼저 Store 목록을 가져온 후 transPlanTsrListParam의 내용에 따라서 최초 LIST_TRANS_PLAN_TSR 호출
    useEffect(() => {
        let diff = new Date().getTime()/1000 - storeList.when;
        if(diff > 300) {
            getArriveInfoList({variables:{dcCd:dcCd}});
        }

        diff = new Date().getTime()/1000 - comInfoListData.when;
        if(diff > 300) {
            getComInfoList();
        }

        diff = new Date().getTime()/1000 - vehInfoListOfDcData.when;
        if(diff > 300) {
            getVehInfoListOfDc({variables:{dcCd:dcCd}});
        }

        if(dcCd === TopCode.DcCode.Adc982) {
            diff = new Date().getTime()/1000 - bulkCompanyProductsListData.when;
            if(diff > 300) {
                getBulkCompanyProductsList({variables:{bulkCompanyId:null, bulkProductsId:null}});
            }
        }
    }, []);

    // trans_plan 테이블에 추가 또는 삭제가 있을 경우 다시 읽는다.
    useEffect(() => {
        /*
        다시 읽기를 실행하는 대부분의 경우, 직접 getTransPlanTsrListNow를 호출하는 대신
        transPlanChangedRepo를 호출하여 transPlanChanged 이벤트를 발생시키고,
        이 이벤트를 감지하여 getTransPlanTsrListNow를 호출하도록 한다.
        왜냐하면, 그로 인해 가져오는 데이터는 이 컴포넌트 내부뿐만 아니라, 다른 화면에서도
        변경될 수 있고, 그 결과 transPlanChangedRepo 변화를 통해 이 화면에서도
        데이터를 반영해 줘야 한다. 즉, 하나의 데이터가 여러곳에서 변경되는데, 그 변경을
        감지해 반영하는 방법을 통일시키기 위해서이다.
        단, 하나의 예외는 UI에서 storeId와 planDate를 변경하는 경우인데, 이 것은
        데이터를 변경한 것이 아니라 단순히 조회조건을 변경한 것이기 때문이다.
        */
        if(skipInitTransPlanChangedRef.current) {
            skipInitTransPlanChangedRef.current = false;
            return;
        }
        const { storeId, planDate } = transPlanTsrListParamRef.current;
        if(storeId && planDate) {
            getTransPlanTsrListNow(storeId, planDate);
        }
    }, [transPlanChanged]);
    
    useEffect(() => {
        const diff = new Date().getTime()/1000 - dcInfoListData.when;
        if(diff > 600 || dcInfoListData.records.length === 0) getDcInfoList();
    }, [dcInfoListData, getDcInfoList]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        lastReloadRef.current = new Date().getTime()/1000;
        if(data.transPlanTsrList) {
            transPlanTsrListRepo({when:new Date().getTime()/1000, records:data.transPlanTsrList});
        }
    };
    
    const onCompleteGetStoreList = (data, clientOption) => {
        if(data.arriveInfoList) {
            if(data.arriveInfoList.length > 0) {
                // sort by storeId (text)
                data.arriveInfoList.sort((a,b) => {
                    if(a.storeId < b.storeId) return -1;
                    if(a.storeId > b.storeId) return 1;
                    return 0;
                });
                if(!transPlanTsrListParam.storeId) {
                    const newParam = {...transPlanTsrListParam, storeId:data.arriveInfoList[0].storeId};
                    transPlanTsrListParamRepo(newParam);
                }
            }
            arriveInfoListToSelectStoreRepo({
                when:new Date().getTime()/1000,
                records:data.arriveInfoList
            });
        }
    };
    
    const onCompleteGetDcList = (data, clientOption) => {
        if(data.dcInfoList) {
            dcInfoListRepo({when:new Date().getTime()/1000, records:data.dcInfoList});
        }
    };
        
    const onCompleteGetComList = (data, option) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };
    
    const onCompleteGetVehInfoList = (data, clientOption) => {
        if(data.vehInfoListOfDc) {
            vehInfoListOfDcRepo({
                when:new Date().getTime()/1000,
                records:devideVehiclesByCompany(data.vehInfoListOfDc, vehSorter)
            });
        }
    };

    // 982 Bulk 상품 목록 조회
    const onCompleteGetBulkCompanyProductsList = (data, clientOption) => {
        if(data.bulkCompanyProductsList) {
            bulkCompanyProductsListRepo({when:new Date().getTime()/1000, records:data.bulkCompanyProductsList});
        }
    };

    const onCompleteSetTruck = (data, clientOption) => {
        if(data.transPlanSetTruck.ok) {
            enqueueSnackbar('차량이 운송요청에 배차되었습니다.', {variant: 'success', autoHideDuration: 1500});
            /* getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate); */
            transPlanChangedRepo({
                planDate:transPlanTsrListParam.planDate,
                storeId:transPlanTsrListParam.storeId,
                now:new Date().getTime()});
        }
        else addAlert({title:"차량배정 오류", message:[data.transPlanSetTruck.message]});
    };

    const onCompleteOffTruck = (data, clientOption) => {
        if(data.transPlanOffTruck.ok) {
            enqueueSnackbar('[배차취소] 차량배정이 초기화되었습니다.', {variant: 'success', autoHideDuration: 1500});
            /* getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate); */
            transPlanChangedRepo({
                planDate:transPlanTsrListParam.planDate,
                storeId:transPlanTsrListParam.storeId,
                now:new Date().getTime()});
        }
        else addAlert({title:"차량배정 초기화 오류", message:[data.transPlanOffTruck.message]});
    };

    const onCompleteSetCompany = (data, clientOption) => {
        if(data.transPlanSetCompany.ok) {
            enqueueSnackbar('운송사가 변경되었습니다.', {variant: 'success', autoHideDuration: 1500});
            /* getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate); */
            transPlanChangedRepo({
                planDate:transPlanTsrListParam.planDate,
                storeId:transPlanTsrListParam.storeId,
                now:new Date().getTime()});
        }
        else addAlert({title:"운송사 변경 오류", message:[data.transPlanSetCompany.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 2) {
            // Too short to reload
            enqueueSnackbar('2초 이내에 재조회는 지양해 주세요.', {variant:'info', autoHideDuration:2000});
        }
        else if(transPlanTsrListParam.storeId && transPlanTsrListParam.planDate) {
            getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate);
        }
    };
    
    const onStoreSelected = (id, value) => {
        const newParam = {...transPlanTsrListParam, storeId:value};
        transPlanTsrListParamRepo(newParam);
    };

    const onChangePlanDate = (id, value) => {
        const newParam = {...transPlanTsrListParam, planDate:value};
        transPlanTsrListParamRepo(newParam);
    };

    const onCompleteTsrMutation = () => {
        /* getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate); */
        transPlanChangedRepo({
                planDate:transPlanTsrListParam.planDate,
                storeId:transPlanTsrListParam.storeId,
                now:new Date().getTime()});
    };

    const onRequestSetTruck = (param) => {
        setTransPlanTruck({variables:{transPlan:param}});
    };

    const onRequestOffTruck = (planId) => {
        offTransPlanTruck({variables:{planId:planId}});
    };

    const onRequestSetCompany = (param) => {
        setTransPlanCompany({variables:{transPlan:param}});
    };

    const loadingAny
        = responseList.loading // || responseItem.loading
        || responseStoreList.loading
        || responseBulkCompanyProductsList.loading
        || responseComList.loading
        || responseVehInfoList.loading
        || responseSetTruck.loading
        || responseOffTruck.loading
        || responseSetCompany.loading;

    const holdMutation
        = responseList.loading
        || responseSetTruck.loading
        || responseOffTruck.loading
        || responseSetCompany.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <VzmDateField
                        dict={{
                            id: 'planDate',
                            label: '출고일자'
                        }}
                        value={transPlanTsrListParam.planDate || ''}
                        error={!IsValid.date(transPlanTsrListParam.planDate)}
                        onChange={onChangePlanDate}
                    />
                    <AutoTextField
                        dict={{
                            id:'storeId',
                            label:'도착지',
                            labelKey:'storeName',
                            getOptionLabel: (opt) => opt.storeName + ' [' + opt.storeId + ']'
                        }}
                        value={transPlanTsrListParam.storeId || ''}
                        list={storeList.records}
                        disabled={responseStoreList.loading}
                        style={{minWidth:300, marginLeft: 10}}
                        listBoxProps={{style: {minHeight:'70vh'}}}
                        onChange={onStoreSelected}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <ContentBody style={{
                    paddingLeft:5, whiteSpace: 'nowrap', overflowX: 'auto'
                }}
            >
                {
                    transPlanTsrListData.records.length > 0 &&
                    <Box style={{borderBottom: AppStyle.BorderCCC}}>
                    {
                        transPlanTsrListData.records.map((item, idx)=>{
                            return(
                                <TruckerTransPlanItem key={item.hocha}
                                    itemIndex={idx}
                                    item={item}
                                    comInfoList={comInfoList}
                                    holdMutation={holdMutation}
                                    onCompleteMutation={onCompleteTsrMutation}
                                    onRequestSetTruck={onRequestSetTruck}
                                    onRequestOffTruck={onRequestOffTruck}
                                    onRequestSetCompany={onRequestSetCompany}
                                />
                            )
                        })
                    }
                    {
                        transPlanTsrListData.records.length > 0 &&
                        transPlanTsrListData.records.length < 5 &&
                        Array.from(Array(5 - transPlanTsrListData.records.length))
                        .map((_, index) => {
                            return(
                                <div key={index}
                                    style={{
                                        display:'inline-flex',
                                        flexDirection: 'column',
                                        width: 220,
                                        textAlign:'center',
                                        alignItems:'center',
                                        color: AppStyle.SecondaryRGB,
                                        opacity: 0.4,
                                    }}
                                >
                                    <div style={{height: 250,}}>&nbsp;</div>
                                    <LocalShippingIcon
                                        fontSize={AppWord.LARGE}
                                    />
                                    <div
                                        style={{
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: AppStyle.SecondaryRGB,
                                            opacity: 0.3,
                                            width: '90%'
                                        }}
                                    >&nbsp;</div>
                                </div>
                            )
                        })
                    }
                    </Box>
                }
                {
                    transPlanTsrListData.records.length === 0 &&
                    <Box style={{
                        display:'flex',
                        flexDirection:'column',
                        justifyContent:'center',
                        alignItems:'center',
                        paddingTop:50
                    }}>
                        <img src={StoreHouseSvg} alt="_" height="320" />
                        <div style={{marginTop:20, fontSize:'2rem', color:'#fa3'}}>
                            해당 점포로의 배차내역이 없습니다.
                        </div>
                    </Box>
                }
            </ContentBody>
        </ContentConainer>
    );
}
